import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { sendChat, type ChatRecommendation, type ChatTurn } from '@/api';
import { isLoggedIn } from '@/auth/token';

export default function MatchChat({
  onUse,
  busyId,
}: {
  onUse: (outcomeId: string) => void;
  busyId: string | null;
}) {
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recs, setRecs] = useState<ChatRecommendation[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const logged = isLoggedIn();

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || typing) return;
    const next: ChatTurn[] = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setTyping(true);
    setError(null);
    try {
      const reply = await sendChat({
        mode: 'match',
        messages: next,
        project: { title: '', description: '', outcome_type: '', nodes: [], edges: [] },
      });
      setMessages([...next, { role: 'assistant', content: reply.content || '' }]);
      setRecs(reply.recommendations || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'chat failed');
    } finally {
      setTyping(false);
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }));
    }
  };

  return (
    <div data-testid="match-chat" className="flex flex-col h-full min-h-[28rem] bg-surface border border-border rounded-xl">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-sm font-semibold text-textp">What outcome do you want?</h2>
        <p className="text-xs text-texts mt-1">Describe the job. The harness recommends from the certified list only.</p>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && !typing && (
          <p className="text-sm text-texts">
            Try &ldquo;review IAM policies before merge&rdquo; or pick a card on the right.
          </p>
        )}
        {messages.map((m, i) => (
          <div key={`${m.role}-${i}`} className={m.role === 'user' ? 'text-sm text-textp' : 'text-sm text-texts'}>
            <div className="text-[10px] uppercase tracking-wider text-texts mb-1">{m.role === 'user' ? 'You' : 'Sourcetape'}</div>
            <div className="whitespace-pre-wrap">{m.content}</div>
          </div>
        ))}
        {typing && <div className="text-xs text-texts">Matching certified outcomes…</div>}
        {recs.length > 0 && (
          <div className="space-y-2 pt-2" data-testid="match-recommendations">
            {recs.map((r) => (
              <div key={r.id} className="border border-border rounded-lg px-3 py-2">
                <div className="text-sm font-medium text-textp">{r.name}</div>
                {r.why && <p className="text-xs text-texts mt-1">{r.why}</p>}
                <button
                  type="button"
                  data-testid="use-playbook"
                  disabled={busyId === r.id}
                  onClick={() => onUse(r.id)}
                  className="mt-2 text-xs font-medium text-accent-text bg-accent hover:bg-accent-hover disabled:opacity-60 px-3 py-1.5 rounded-md"
                >
                  {busyId === r.id ? 'Opening…' : 'Use this playbook'}
                </button>
              </div>
            ))}
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="px-4 py-3 border-t border-border">
        {!logged ? (
          <p className="text-xs text-texts">
            <Link to={`/register?next=${encodeURIComponent('/playbooks')}`} className="text-accent hover:underline">
              Create an account
            </Link>
            {' '}to chat, or pick a certified outcome.
          </p>
        ) : (
          <>
            {error && <div className="mb-2 text-xs text-error">{error}</div>}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void send(input);
              }}
              className="flex gap-2"
            >
              <input
                data-testid="match-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="The outcome I want is…"
                className="flex-1 bg-bg border border-border rounded-md px-3 py-2 text-sm text-textp outline-none focus:border-accent"
              />
              <button
                type="submit"
                disabled={typing || !input.trim()}
                className="text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover disabled:opacity-60 px-3 py-2 rounded-md"
              >
                Send
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
